/**
 * Web Audio API engine for short one-shot SFX (decode once, play via BufferSourceNode).
 */
import { resolveSoundFileUrl } from "../assets/sound-urls.js";

/**
 * @param {Record<string, string>} manifest name → filename
 * @param {{
 *   resolveUrl?: (file: string) => string,
 *   getSoundGain?: (name: string) => number,
 *   clampVolume?: (volume: number) => number,
 *   fetchFn?: typeof fetch,
 *   createContext?: () => AudioContext|null,
 * }} [opts]
 */
export function createSfxEngine(manifest, opts) {
  opts = opts || {};
  var resolveUrl = opts.resolveUrl || resolveSoundFileUrl;
  var getSoundGain = opts.getSoundGain || function () { return 1; };
  var clampVolume = opts.clampVolume || function (volume) {
    var n = Number(volume);
    if (!isFinite(n)) return 0;
    return Math.max(0, Math.min(1, n));
  };
  var fetchFn = opts.fetchFn || (typeof fetch === "function" ? fetch.bind(globalThis) : null);

  var ctx = null;
  var masterGain = null;
  var buffers = Object.create(null);
  /** @type {Record<string, ArrayBuffer>} decoded on unlock — avoids creating AudioContext at boot */
  var rawBuffers = Object.create(null);
  var readyPromise = null;
  var muted = false;
  var focusPaused = false;
  var unlocked = false;
  var masterVolume = 0.5;
  /** @type {AudioBufferSourceNode[]} */
  var activeSources = [];
  /** Longest {@link tryUnlockAsync} waits for a context to report "running". */
  var BOOT_START_WAIT_MS = 300;

  function defaultCreateContext() {
    if (typeof window === "undefined") return null;
    var Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return null;
    return new Ctx();
  }

  var createContext = opts.createContext || defaultCreateContext;

  function ensureContext() {
    if (ctx) return ctx;
    try {
      ctx = createContext();
    } catch (e) {
      // Browsers cap live AudioContexts; a throw here must not take the game down with it.
      ctx = null;
    }
    if (!ctx) return null;
    masterGain = ctx.createGain();
    masterGain.gain.value = muted ? 0 : masterVolume;
    masterGain.connect(ctx.destination);
    return ctx;
  }

  function pruneActiveSources() {
    activeSources = activeSources.filter(function (src) {
      try {
        return src && src.buffer;
      } catch (e) {
        return false;
      }
    });
  }

  function stopActiveSources() {
    for (var i = 0; i < activeSources.length; i++) {
      try {
        activeSources[i].stop(0);
      } catch (e) { /* ignore */ }
    }
    activeSources = [];
  }

  function decodeWithOfflineContext(arrayBuffer) {
    if (!arrayBuffer || typeof window === "undefined") return Promise.resolve(null);
    var OfflineCtx = window.OfflineAudioContext || window.webkitOfflineAudioContext;
    if (!OfflineCtx) return Promise.resolve(null);
    try {
      var offline = new OfflineCtx(1, 1, 44100);
      if (typeof offline.decodeAudioData !== "function") return Promise.resolve(null);
      var copy = arrayBuffer.slice(0);
      var decoded = offline.decodeAudioData(copy);
      if (decoded && typeof decoded.then === "function") {
        return decoded.catch(function () { return null; });
      }
      return new Promise(function (resolve, reject) {
        offline.decodeAudioData(copy, resolve, function () { reject(new Error("decode failed")); });
      }).catch(function () { return null; });
    } catch (e) {
      return Promise.resolve(null);
    }
  }

  function decodeWithMainContext(arrayBuffer) {
    var audioCtx = ensureContext();
    if (!audioCtx || !arrayBuffer || typeof audioCtx.decodeAudioData !== "function") {
      return Promise.resolve(null);
    }
    var copy = arrayBuffer.slice(0);
    return new Promise(function (resolve) {
      var onSuccess = function (buffer) { resolve(buffer); };
      var onError = function () { resolve(null); };
      try {
        var decoded = audioCtx.decodeAudioData(copy, onSuccess, onError);
        if (decoded && typeof decoded.then === "function") {
          decoded.then(onSuccess, onError);
        }
      } catch (e2) {
        onError();
      }
    });
  }

  function decodeStoredBuffer(name) {
    var arrayBuffer = rawBuffers[name];
    if (!arrayBuffer) return Promise.resolve(buffers[name] || null);
    return decodeWithMainContext(arrayBuffer).then(function (buffer) {
      if (buffer) {
        buffers[name] = buffer;
        delete rawBuffers[name];
      }
      return buffer;
    });
  }

  function decodeAllPending() {
    var names = Object.keys(rawBuffers);
    if (names.length === 0) return Promise.resolve();
    return Promise.all(names.map(function (name) { return decodeStoredBuffer(name); })).then(function () {});
  }

  function fetchUrl(url, timeoutMs) {
    if (!fetchFn || !url) return Promise.resolve(null);
    var limit = timeoutMs != null ? timeoutMs : 10000;
    return new Promise(function (resolve) {
      var settled = false;
      function done(result) {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        resolve(result);
      }
      var timer = setTimeout(function () { done(null); }, limit);
      fetchFn(url)
        .then(function (res) {
          // Capacitor iOS answers capacitor:// media files (.mp3, …) with a non-HTTP response,
          // so fetch sees status 0 / ok false. Accept that when the response is readable.
          var localOk = res && res.status === 0 && res.type !== "opaque";
          if (!res || (!res.ok && !localOk)) {
            done(null);
            return null;
          }
          return res.arrayBuffer();
        })
        .then(function (buf) {
          done(buf && buf.byteLength > 0 ? buf : null);
        })
        .catch(function () { done(null); });
    });
  }

  function decodeUrl(url, timeoutMs) {
    return fetchUrl(url, timeoutMs).then(function (arrayBuffer) {
      if (!arrayBuffer) return null;
      return decodeWithOfflineContext(arrayBuffer).then(function (buffer) {
        return buffer ? buffer : arrayBuffer;
      });
    });
  }

  function init(timeoutMs) {
    if (readyPromise) return readyPromise;
    if (typeof window === "undefined") {
      readyPromise = Promise.resolve();
      return readyPromise;
    }
    var names = Object.keys(manifest || {});
    var seen = Object.create(null);
    var tasks = [];
    for (var i = 0; i < names.length; i++) {
      var name = names[i];
      var file = manifest[name];
      if (!file || seen[file]) continue;
      seen[file] = true;
      var url = resolveUrl(file);
      if (!url) continue;
      (function (soundName, soundUrl) {
        tasks.push(
          decodeUrl(soundUrl, timeoutMs).then(function (result) {
            if (!result) {
              if (typeof console !== "undefined" && console.warn) {
                console.warn("[sfx-engine] failed to load SFX:", soundName, soundUrl);
              }
              return;
            }
            if (result instanceof ArrayBuffer) {
              rawBuffers[soundName] = result;
              return;
            }
            buffers[soundName] = result;
          })
        );
      })(name, url);
    }
    readyPromise = Promise.all(tasks).then(function () {
      for (var j = 0; j < names.length; j++) {
        var n = names[j];
        if (buffers[n] || rawBuffers[n]) continue;
        var f = manifest[n];
        if (!f) continue;
        for (var k = 0; k < names.length; k++) {
          var other = names[k];
          if (manifest[other] !== f) continue;
          if (buffers[other]) {
            buffers[n] = buffers[other];
            break;
          }
          if (rawBuffers[other]) {
            rawBuffers[n] = rawBuffers[other];
            break;
          }
        }
      }
    });
    return readyPromise;
  }

  function setMasterGain(volume) {
    masterVolume = clampVolume(volume);
    if (masterGain) {
      try {
        masterGain.gain.value = muted ? 0 : masterVolume;
      } catch (e) { /* ignore */ }
    }
  }

  function setMuted(value) {
    muted = !!value;
    if (masterGain) {
      try {
        masterGain.gain.value = muted ? 0 : masterVolume;
      } catch (e) { /* ignore */ }
    }
    if (muted) stopActiveSources();
  }

  function setFocusPaused(paused) {
    focusPaused = !!paused;
    if (focusPaused) {
      stopActiveSources();
      suspendContext();
      return;
    }
    if (unlocked) resumeContext();
  }

  function setUnlocked(value) {
    unlocked = !!value;
  }

  function isUnlocked() {
    return unlocked;
  }

  /**
   * Play an HTML media element through this context's destination.
   * Mobile Safari ignores HTMLMediaElement.volume; a GainNode does not.
   * The node is not chained through the SFX master, so music and effects stay independent.
   * @param {HTMLAudioElement} audio
   * @returns {GainNode|null}
   */
  function connectMediaElement(audio) {
    if (!audio) return null;
    if (audio._bgmOutputGain) return audio._bgmOutputGain;
    var audioCtx = ensureContext();
    if (!audioCtx || typeof audioCtx.createMediaElementSource !== "function") return null;
    try {
      var source = audioCtx.createMediaElementSource(audio);
      var gain = audioCtx.createGain();
      gain.gain.value = 1;
      source.connect(gain);
      gain.connect(audioCtx.destination);
      audio._bgmOutputGain = gain;
      return gain;
    } catch (e) {
      return null;
    }
  }

  function resumeContext() {
    var audioCtx = ensureContext();
    if (!audioCtx || typeof audioCtx.resume !== "function") {
      return Promise.resolve(false);
    }
    try {
      var p = audioCtx.resume();
      if (p && typeof p.then === "function") {
        return p.then(function () { return audioCtx.state === "running"; });
      }
      return Promise.resolve(audioCtx.state === "running");
    } catch (e) {
      return Promise.resolve(false);
    }
  }

  function suspendContext() {
    if (!ctx || typeof ctx.suspend !== "function") return Promise.resolve();
    try {
      var p = ctx.suspend();
      if (p && typeof p.catch === "function") p.catch(function () {});
    } catch (e) { /* ignore */ }
    return Promise.resolve();
  }

  /**
   * Resume AudioContext after a user gesture.
   * @returns {Promise<boolean>}
   */
  function unlock() {
    unlocked = true;
    // A context left suspended by the boot probe already exists: resume it while the gesture is
    // still on the stack, then again once any pending buffers have decoded.
    if (ctx) resumeContext();
    return decodeAllPending().then(function () {
      return resumeContext();
    }).then(function () {
      return true;
    });
  }

  /** `navigator.getAutoplayPolicy` (Firefox) says up front when a new context would stay suspended. */
  function autoplayPolicyDisallowsContext() {
    try {
      if (typeof navigator === "undefined" || typeof navigator.getAutoplayPolicy !== "function") return false;
      return navigator.getAutoplayPolicy("audiocontext") === "disallowed";
    } catch (e) {
      return false;
    }
  }

  /**
   * Resolves true once `audioCtx` reports "running", false after `waitMs`.
   * Firefox flips an allowed new context to "running" a moment after construction; Chrome does it at once.
   * @param {AudioContext} audioCtx
   * @param {number} waitMs
   * @returns {Promise<boolean>}
   */
  function waitForRunning(audioCtx, waitMs) {
    if (audioCtx.state === "running") return Promise.resolve(true);
    if (!(waitMs > 0)) return Promise.resolve(false);
    return new Promise(function (resolve) {
      var settled = false;
      function done() {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        try {
          audioCtx.removeEventListener("statechange", onStateChange);
        } catch (e) { /* ignore */ }
        resolve(audioCtx.state === "running");
      }
      function onStateChange() {
        if (audioCtx.state === "running") done();
      }
      var timer = setTimeout(done, waitMs);
      try {
        audioCtx.addEventListener("statechange", onStateChange);
      } catch (e2) { /* the timeout still settles it */ }
    });
  }

  /**
   * Probe whether SFX can play without a stored gesture unlock.
   * Never calls AudioContext.resume() — that requires a user gesture.
   * With `createIfMissing`, builds the context first: the browser starts it running only when its
   * autoplay policy already allows sound (e.g. a reload of a page the player has tapped). A context
   * that stays suspended is kept for the gesture unlock to resume.
   * @param {{ createIfMissing?: boolean, waitMs?: number }} [probeOpts]
   * @returns {Promise<boolean>}
   */
  function tryUnlockAsync(probeOpts) {
    probeOpts = probeOpts || {};
    var audioCtx = ctx;
    if (!audioCtx && probeOpts.createIfMissing && !autoplayPolicyDisallowsContext()) {
      audioCtx = ensureContext();
    }
    if (!audioCtx) return Promise.resolve(false);
    var waitMs = probeOpts.waitMs != null ? probeOpts.waitMs : BOOT_START_WAIT_MS;
    return waitForRunning(audioCtx, waitMs).then(function (running) {
      if (!running || ctx !== audioCtx) return false;
      unlocked = true;
      if (focusPaused) suspendContext();
      decodeAllPending().catch(function () {});
      return true;
    }, function () {
      return false;
    });
  }

  /**
   * @param {string} name
   * @param {{ volume?: number, playbackRate?: number, ignoreGuards?: boolean }} [playOpts]
   */
  function play(name, playOpts) {
    playOpts = playOpts || {};
    if (!playOpts.ignoreGuards) {
      if (!unlocked || muted || focusPaused) return;
    }
    var buffer = buffers[name];
    if (!buffer) return;
    var audioCtx = ensureContext();
    if (!audioCtx || !masterGain) return;

    try {
      var source = audioCtx.createBufferSource();
      source.buffer = buffer;
      if (playOpts.playbackRate != null && isFinite(Number(playOpts.playbackRate))) {
        source.playbackRate.value = Number(playOpts.playbackRate);
      }

      var perSoundGain = audioCtx.createGain();
      var baseVol = playOpts.volume != null ? playOpts.volume : masterVolume;
      perSoundGain.gain.value = clampVolume(baseVol * getSoundGain(name));
      source.connect(perSoundGain);
      perSoundGain.connect(masterGain);

      source.onended = function () {
        var idx = activeSources.indexOf(source);
        if (idx >= 0) activeSources.splice(idx, 1);
      };

      source.start(0);
      activeSources.push(source);
      pruneActiveSources();
    } catch (e) { /* ignore */ }
  }

  function dispose() {
    stopActiveSources();
    buffers = Object.create(null);
    rawBuffers = Object.create(null);
    readyPromise = null;
    if (ctx && typeof ctx.close === "function") {
      try {
        ctx.close();
      } catch (e2) { /* ignore */ }
    }
    ctx = null;
    masterGain = null;
  }

  function hasBuffer(name) {
    return !!(buffers[name] || rawBuffers[name]);
  }

  function getContextState() {
    return ctx ? ctx.state : null;
  }

  return {
    init: init,
    unlock: unlock,
    tryUnlockAsync: tryUnlockAsync,
    play: play,
    setMasterGain: setMasterGain,
    connectMediaElement: connectMediaElement,
    resumeContext: resumeContext,
    setMuted: setMuted,
    setFocusPaused: setFocusPaused,
    setUnlocked: setUnlocked,
    isUnlocked: isUnlocked,
    dispose: dispose,
    hasBuffer: hasBuffer,
    getContextState: getContextState,
    /** @internal test helper */
    _getActiveSourceCount: function () {
      pruneActiveSources();
      return activeSources.length;
    },
  };
}